import { useConfig } from "$global-providers/config-provider/provider";
import { TERRITORY } from "$routes/_lib/constants";
import { currencyFormatFor } from "$routes/_lib/currency-formatter";
import { Decimal, type Numeric } from "decimal.js-light";
import { useMemo } from "react";

export enum LOCALE_LANG {
  EN_NG = "en-NG",
  EN_KE = "en-KE",
  EN_PK = "en-PK",
  EN_LR = "en-LR",
}

const UNSUPPORTED_TERRITORY_ERROR =
  "No currency supported for this territory. Please contact support.";

interface CurrencyFormat {
  compact: boolean;
  locale: LOCALE_LANG;
}
export interface Currency {
  format: CurrencyFormat;
  decimal?: number;
  code: string;
  uuid: string;
}

/**
 * A currency resolved from the active territory. Carries the local unit names
 * (`major`/`subunit`) so the names stay co-located with the currency they
 * describe and can't drift from the rest of the territory config.
 */
interface LocalisedCurrency extends Currency {
  /** Local name of the main currency unit, e.g. "naira". */
  major: string;
  /** Local name of the smallest unit prices are stored in, e.g. "kobo". */
  subunit: string;
}

function useLocalisedCurrency(): LocalisedCurrency {
  const config = useConfig();
  const { territory } = config;

  const currencyMap: Record<typeof territory, LocalisedCurrency> = {
    [TERRITORY.NIGERIA]: {
      format: {
        compact: false,
        locale: LOCALE_LANG.EN_NG,
      },
      decimal: 2,
      code: "NGN",
      uuid: "6ae235f2-e345-404a-aab4-4e43f4faac5f",
      major: "naira",
      subunit: "kobo",
    },
    [TERRITORY.KENYA]: {
      decimal: 2,
      code: "KES",
      format: {
        compact: true,
        locale: LOCALE_LANG.EN_KE,
      },
      uuid: "6d300ea6-8421-4678-88ad-7c33a71bb877",
      major: "shilling",
      subunit: "cents",
    },
    [TERRITORY.PAKISTAN]: {
      decimal: 2,
      format: {
        compact: false,
        locale: LOCALE_LANG.EN_PK,
      },
      code: "PKR",
      uuid: "d38472d2-02e8-437a-b55a-565e19516c8a",
      major: "rupee",
      subunit: "paisa",
    },
    [TERRITORY.LIBERIA]: {
      decimal: 2,
      format: {
        compact: false,
        locale: LOCALE_LANG.EN_LR,
      },
      code: "LRD",
      uuid: "d57403c2-42e1-4a37-9a90-8e58e074d14b",
      major: "Liberian dollar",
      subunit: "cents",
    },
  };

  const currency = currencyMap[territory];
  if (!currency) {
    throw new Error(UNSUPPORTED_TERRITORY_ERROR);
  }

  return currency;
}

export default function useCurrencyFormat(overrideCurrency?: Currency) {
  const billingCurrency = useLocalisedCurrency();
  const currency = overrideCurrency ?? billingCurrency;

  return useMemo(() => currencyFormatFor(currency), [currency]);
}
const CURRENCY_BASE_DEFAULT = 10;
const DECIMALS_PLACES_DEFAULT = 2;

/**
 * @return amountInPence optional as syntactic sugar - allows forms to use this method directly without
 * first performing undefined checks
 */
export const penceToPounds =
  (currency: Currency) => (amountInPence?: Numeric) => {
    if (!amountInPence && amountInPence !== 0) {
      return undefined;
    }

    const currencyDecimals = currency?.decimal ?? DECIMALS_PLACES_DEFAULT;
    const amountInPenceDecimal = new Decimal(amountInPence);
    return amountInPenceDecimal
      .div(CURRENCY_BASE_DEFAULT ** currencyDecimals)
      .toNumber();
  };

export function useConvertFromPenceFactory(): (
  pounds?: Numeric,
) => number | undefined {
  const currency = useLocalisedCurrency();
  return penceToPounds(currency);
}

/**
 * @return amountInPounds optional as syntactic sugar - allows forms to use this method directly without
 * first performing undefined checks
 */
export const poundsToPence =
  (currency: Currency) =>
  (amountInPounds?: Numeric): number | undefined => {
    if (!amountInPounds && amountInPounds !== 0) {
      return undefined;
    }

    const currencyDecimals = currency?.decimal ?? DECIMALS_PLACES_DEFAULT;
    const amountInPoundsDecimal = new Decimal(amountInPounds);
    return amountInPoundsDecimal
      .mul(CURRENCY_BASE_DEFAULT ** currencyDecimals)
      .toInteger()
      .toNumber();
  };

export function useConvertFromPoundsFactory(): (
  pence?: Numeric,
) => number | undefined {
  const currency = useLocalisedCurrency();
  return poundsToPence(currency);
}

export interface CurrencyUnitNames {
  /** Local name of the main currency unit, e.g. "naira". */
  major: string;
  /** Local name of the smallest unit that prices are stored in, e.g. "kobo". */
  subunit: string;
  /** How many subunits make one major unit, e.g. 100. */
  subunitsPerMajor: number;
}

export function useCurrencyUnitNames(): CurrencyUnitNames {
  const { major, subunit, decimal } = useLocalisedCurrency();
  const decimals = decimal ?? DECIMALS_PLACES_DEFAULT;
  return {
    major,
    subunit,
    subunitsPerMajor: CURRENCY_BASE_DEFAULT ** decimals,
  };
}
